import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  Video, Activity, MessageSquare, GitCompareArrows, LineChart,
  FileText, ListChecks, ShoppingBag, Users, Mic, Check, MapPin, ArrowRight, ChevronLeft, ChevronRight,
} from "lucide-react";

const MotionLink = motion.create(Link);

/**
 * FeatureShowcase — the landing page's "what this product actually does"
 * section.
 *
 * Every claim here maps to shipped code. Nothing aspirational:
 *   - shot-by-shot + timestamps ....... MatchInsights.jsx / AnalyzePage results
 *   - posture skeleton + joint angles . ai/poseOverlay.js, PoseOverlayModal.jsx
 *     (gated by ai/posturePolicy.js — racket/ball sports ONLY; the card says so)
 *   - ask coach / live voice coach .... VirtualCoach.jsx, LiveVoiceCoach.jsx
 *   - re-analyze & compare ............ FormComparisonModal.jsx, /compare-analyses
 *   - progress & history .............. ProgressPage.jsx
 *   - PDF coach report ................ lib/coachReport.js
 *   - training plans / gear ........... TrainingPlan + EquipmentRecommendModal
 *   - host & join games ............... CommunityPage
 *
 * The inline mockups are CSS/SVG only (no images, no deps) and are labelled
 * "Sample" so nobody reads them as a real user's numbers.
 */

const SampleTag = ({ children = "Sample" }) => (
  <span className="absolute bottom-2.5 right-3 text-[10px] uppercase tracking-widest font-semibold text-zinc-400 bg-zinc-950/80 border border-zinc-800 rounded-full px-2 py-0.5">
    {children}
  </span>
);

/* ---------- mockup: shot-by-shot breakdown ---------- */
function ShotBreakdownMock() {
  const shots = [
    { t: "0:08", name: "Smash", intent: "Finish the rally", ok: true },
    { t: "0:14", name: "Net Drop", intent: "Force a lift", ok: true },
    { t: "0:21", name: "Clear", intent: "Reset position", ok: false },
  ];
  return (
    <div className="relative rounded-2xl border border-zinc-800 bg-zinc-950/60 p-3 sm:p-4 overflow-hidden">
      <SampleTag />
      {/* timeline strip */}
      <div className="relative h-8 rounded-lg bg-zinc-900 border border-zinc-800 mb-3 overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r from-lime-400/25 to-transparent" />
        {[18, 42, 63].map((p) => (
          <div key={p} className="absolute inset-y-1 w-0.5 rounded-full bg-lime-400" style={{ left: `${p}%` }} />
        ))}
        <div className="absolute inset-y-0 flex items-center left-2 text-[9px] font-mono text-zinc-500">00:00</div>
        <div className="absolute inset-y-0 flex items-center right-2 text-[9px] font-mono text-zinc-500">00:30</div>
      </div>
      <div className="space-y-1.5">
        {shots.map((s) => (
          <div key={s.t} className="flex items-center gap-2 rounded-lg bg-zinc-900/80 border border-zinc-800/80 px-2.5 py-2">
            <span className="font-mono text-[10px] text-lime-400 tabular-nums shrink-0">{s.t}</span>
            <span className="text-xs font-semibold text-white shrink-0">{s.name}</span>
            <span className="text-[10px] text-zinc-500 truncate hidden sm:inline">{s.intent}</span>
            <span className={`ml-auto shrink-0 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
              s.ok ? "text-lime-300 bg-lime-400/10" : "text-amber-300 bg-amber-400/10"
            }`}>{s.ok ? "Won" : "Lost"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- mockup: posture skeleton + joint angles ---------- */
function PostureMock() {
  return (
    <div className="relative rounded-2xl border border-zinc-800 bg-zinc-950/60 p-3 overflow-hidden">
      <SampleTag />
      <svg viewBox="0 0 160 150" className="w-full h-32" aria-hidden="true">
        <g stroke="#a3e635" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.9">
          <path d="M78 34 L78 74" />
          <path d="M78 44 L54 60 L44 84" />
          <path d="M78 44 L104 34 L118 16" />
          <path d="M78 74 L60 106 L58 134" />
          <path d="M78 74 L98 104 L104 134" />
        </g>
        {[[78, 28], [78, 44], [54, 60], [44, 84], [104, 34], [118, 16], [78, 74], [60, 106], [58, 134], [98, 104], [104, 134]].map(([cx, cy]) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="3.2" fill="#0a0a0a" stroke="#a3e635" strokeWidth="1.6" />
        ))}
        {/* corrected position for the hitting arm — dashed, like the in-app view */}
        <path d="M78 44 L98 22 L106 4" stroke="#fbbf24" strokeWidth="2" strokeDasharray="4 3" strokeLinecap="round" fill="none" />
        <circle cx="98" cy="22" r="2.6" fill="#fbbf24" />
        <text x="110" y="24" fill="#fbbf24" fontSize="8" fontFamily="ui-sans-serif, system-ui">target</text>
      </svg>
      <div className="space-y-1.5 mt-1">
        {[
          { label: "Elbow at contact", val: "152°", ideal: "140–165°", ok: true },
          { label: "Shoulder rotation", val: "71°", ideal: "80–100°", ok: false },
        ].map((r) => (
          <div key={r.label} className="flex items-center gap-2 text-[10px]">
            <span className="text-zinc-400 truncate">{r.label}</span>
            <span className={`ml-auto font-mono font-bold ${r.ok ? "text-lime-400" : "text-amber-400"}`}>{r.val}</span>
            <span className="text-zinc-600 font-mono">/ {r.ideal}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- mockup: coach chat ---------- */
function CoachChatMock() {
  return (
    <div className="relative rounded-2xl border border-zinc-800 bg-zinc-950/60 p-3 space-y-2 overflow-hidden">
      <SampleTag />
      <div className="ml-auto max-w-[80%] rounded-2xl rounded-br-sm bg-zinc-800 px-3 py-2 text-[11px] text-zinc-200">
        Why did my clears keep landing short?
      </div>
      <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-lime-400/10 border border-lime-400/20 px-3 py-2 text-[11px] text-lime-100">
        You're contacting the shuttle in front of your body instead of overhead — that costs you depth. Try the shadow-clear drill in your plan.
      </div>
      <div className="flex items-center gap-2 pt-1">
        <div className="flex-1 h-7 rounded-full bg-zinc-900 border border-zinc-800 flex items-center px-3 text-[10px] text-zinc-600">
          Ask about this session…
        </div>
        <div className="w-7 h-7 rounded-full bg-lime-400 flex items-center justify-center shrink-0">
          <Mic className="w-3.5 h-3.5 text-black" />
        </div>
      </div>
    </div>
  );
}

/* ---------- mockup: compare two sessions ---------- */
function CompareMock() {
  return (
    <div className="relative rounded-2xl border border-zinc-800 bg-zinc-950/60 p-3 overflow-hidden">
      <SampleTag />
      <div className="flex items-center gap-2">
        <div className="flex-1 rounded-xl bg-zinc-900 border border-zinc-800 p-2.5 text-center">
          <p className="text-[9px] uppercase tracking-wider text-zinc-500">Session 1</p>
          <p className="font-heading font-black text-2xl text-zinc-300">61</p>
        </div>
        <div className="shrink-0 text-lime-400">
          <GitCompareArrows className="w-4 h-4" />
        </div>
        <div className="flex-1 rounded-xl bg-lime-400/10 border border-lime-400/30 p-2.5 text-center">
          <p className="text-[9px] uppercase tracking-wider text-lime-400/80">Today</p>
          <p className="font-heading font-black text-2xl text-lime-400">74</p>
        </div>
      </div>
      <p className="text-[10px] text-zinc-500 mt-2 text-center">Same drill, three weeks apart — what changed, shot by shot.</p>
    </div>
  );
}

/* ---------- mockup: progress sparkline ---------- */
function ProgressMock() {
  return (
    <div className="relative rounded-2xl border border-zinc-800 bg-zinc-950/60 p-3 overflow-hidden">
      <SampleTag />
      <svg viewBox="0 0 200 62" className="w-full h-16" aria-hidden="true" preserveAspectRatio="none">
        <defs>
          <linearGradient id="fmt-spark" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#a3e635" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#a3e635" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M0,48 L33,42 L66,45 L100,32 L133,26 L166,18 L200,10 L200,62 L0,62 Z" fill="url(#fmt-spark)" />
        <path d="M0,48 L33,42 L66,45 L100,32 L133,26 L166,18 L200,10" fill="none" stroke="#a3e635" strokeWidth="2" strokeLinejoin="round" />
      </svg>
      <div className="flex justify-between text-[9px] text-zinc-600 font-mono mt-1">
        <span>Session 1</span><span>Session 7</span>
      </div>
    </div>
  );
}

/* ---------- mockup: PDF report ---------- */
function ReportMock() {
  return (
    <div className="relative rounded-2xl border border-zinc-800 bg-zinc-950/60 p-3 overflow-hidden">
      <SampleTag />
      <div className="mx-auto w-28 rounded-lg bg-zinc-100 p-2 shadow-lg shadow-black/40 rotate-[-2deg]">
        <div className="h-1.5 w-10 rounded-full bg-lime-500 mb-1.5" />
        <div className="h-1 w-full rounded-full bg-zinc-300 mb-1" />
        <div className="h-1 w-4/5 rounded-full bg-zinc-300 mb-2" />
        <div className="grid grid-cols-3 gap-1 mb-2">
          {[0, 1, 2].map((i) => <div key={i} className="h-4 rounded bg-zinc-200" />)}
        </div>
        <div className="h-1 w-full rounded-full bg-zinc-300 mb-1" />
        <div className="h-1 w-3/4 rounded-full bg-zinc-300 mb-1" />
        <div className="h-1 w-5/6 rounded-full bg-zinc-300" />
      </div>
    </div>
  );
}

/* ---------- mockup: weekly training plan ---------- */
function TrainingPlanMock() {
  const drills = [
    { name: "Shadow footwork", sets: "3 x 10", done: true },
    { name: "Net drop control", sets: "4 x 8", done: true },
    { name: "Smash follow-through", sets: "3 x 12", done: false },
    { name: "Rotation drill", sets: "2 x 15", done: false },
  ];
  const doneCount = drills.filter((d) => d.done).length;
  const pct = Math.round((doneCount / drills.length) * 100);
  return (
    <div className="relative rounded-2xl border border-zinc-800 bg-zinc-950/60 p-3 overflow-hidden">
      <SampleTag />
      <p className="text-[9px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">This week</p>
      <div className="space-y-1.5 mb-3">
        {drills.map((d) => (
          <div key={d.name} className="flex items-center gap-2 rounded-lg bg-zinc-900/80 border border-zinc-800/80 px-2.5 py-1.5">
            <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
              d.done ? "bg-lime-400 border-lime-400" : "border-zinc-600"
            }`}>
              {d.done && <Check className="w-2.5 h-2.5 text-black" strokeWidth={3} />}
            </span>
            <span className={`text-[11px] truncate ${d.done ? "text-zinc-500 line-through" : "text-zinc-200"}`}>{d.name}</span>
            <span className="ml-auto shrink-0 text-[9px] font-mono text-zinc-600">{d.sets}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-zinc-900 border border-zinc-800 overflow-hidden">
          <div className="h-full rounded-full bg-lime-400" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-[9px] font-mono text-zinc-500 shrink-0">{doneCount}/{drills.length} done</span>
      </div>
    </div>
  );
}

/* ---------- mockup: gear recommendations ---------- */
function GearMock() {
  const items = [
    { cat: "Racket", name: "Balance frame, medium flex", match: 92, price: "₹2,400" },
    { cat: "Shoes", name: "Court grip, cushioned sole", match: 87, price: "₹3,100" },
  ];
  return (
    <div className="relative rounded-2xl border border-zinc-800 bg-zinc-950/60 p-3 overflow-hidden">
      <SampleTag />
      <div className="space-y-1.5">
        {items.map((it) => (
          <div key={it.cat} className="flex items-center gap-2.5 rounded-lg bg-zinc-900/80 border border-zinc-800/80 px-2.5 py-2">
            <div className="w-8 h-8 rounded-lg bg-lime-400/10 border border-lime-400/25 flex items-center justify-center shrink-0">
              <ShoppingBag className="w-3.5 h-3.5 text-lime-400" strokeWidth={1.8} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500">{it.cat}</p>
              <p className="text-[11px] text-zinc-200 truncate">{it.name}</p>
            </div>
            <div className="shrink-0 text-right">
              <span className="block text-[9px] font-bold text-lime-400">{it.match}% match</span>
              <span className="block text-[10px] font-mono text-zinc-400">{it.price}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- mockup: host & join games ---------- */
function GameMock() {
  const players = [
    { initials: "AK", tone: "bg-lime-400" },
    { initials: "RS", tone: "bg-sky-400" },
    { initials: "MJ", tone: "bg-zinc-400" },
  ];
  return (
    <div className="relative rounded-2xl border border-zinc-800 bg-zinc-950/60 p-3 overflow-hidden">
      <SampleTag />
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full text-lime-300 bg-lime-400/10 border border-lime-400/25">
          Badminton
        </span>
        <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full text-amber-300 bg-amber-400/10">
          2 spots left
        </span>
      </div>
      <p className="text-[12px] font-semibold text-white mb-1">Doubles · 7:00 PM</p>
      <div className="flex items-center gap-1 text-[10px] text-zinc-500 mb-3">
        <MapPin className="w-3 h-3 shrink-0" />
        <span className="truncate">HSR Layout Sports Complex</span>
      </div>
      <div className="flex items-center -space-x-2">
        {players.map((p) => (
          <div
            key={p.initials}
            className={`w-6 h-6 rounded-full border-2 border-zinc-950 flex items-center justify-center text-[8px] font-bold text-black ${p.tone}`}
          >
            {p.initials}
          </div>
        ))}
        <div className="w-6 h-6 rounded-full border-2 border-dashed border-zinc-700 flex items-center justify-center text-[10px] text-zinc-600 ml-1">
          +
        </div>
      </div>
    </div>
  );
}

const CARDS = [
  {
    key: "analysis",
    short: "Analysis",
    icon: Video,
    tone: "lime",
    title: "Shot-by-shot video analysis",
    desc: "Upload a clip and our AI breaks the session into individual shots with timestamps — what you were trying to do, and how it actually turned out.",
    span: "lg:col-span-2",
    mock: <ShotBreakdownMock />,
    route: "/analyze",
    bullets: [
      "Every shot timestamped on the clip",
      "Intent vs outcome, shot by shot",
      "Works from a single uploaded video",
    ],
  },
  {
    key: "posture",
    short: "Pose fix",
    icon: Activity,
    tone: "lime",
    title: "Posture tracker & pose fix",
    desc: "A skeleton on the contact frame, your joint angles against ideal ranges — and your own pose redrawn in the corrected position.",
    note: "Racket & ball sports only — not claimed for gym lifting.",
    mock: <PostureMock />,
    route: "/analyze",
    bullets: [
      "Skeleton overlay on the contact frame",
      "Your pose next to the corrected pose",
      "Racket & ball sports only",
    ],
  },
  {
    key: "coach",
    short: "Coach",
    icon: MessageSquare,
    tone: "sky",
    title: "Ask Coach & Live Voice Coach",
    desc: "Chat with an AI coach about your own clip, or talk to it out loud between sets.",
    mock: <CoachChatMock />,
    route: "/analyze",
    bullets: [
      "Ask follow-up questions about your clip",
      "Talk to it out loud between sets",
      "Answers grounded in your own session",
    ],
  },
  {
    key: "compare",
    short: "Compare",
    icon: GitCompareArrows,
    tone: "purple",
    title: "Film again & compare",
    desc: "Practise the fix, film the same shot again, and see it side by side with your earlier session.",
    mock: <CompareMock />,
    route: "/analyze",
    bullets: [
      "Upload a new clip of the same shot",
      "Side-by-side with an earlier session",
      "See exactly what changed, shot by shot",
    ],
  },
  {
    key: "progress",
    short: "Progress",
    icon: LineChart,
    tone: "emerald",
    title: "Progress & history",
    desc: "Every analysis is saved to your account, grouped by sport, so trends show up over time.",
    mock: <ProgressMock />,
    route: "/progress",
    bullets: [
      "Every analysis saved automatically",
      "Grouped by sport",
      "Trends visible across sessions",
    ],
  },
  {
    key: "report",
    short: "PDF report",
    icon: FileText,
    tone: "amber",
    title: "PDF coach report",
    desc: "Download a written report of a session — verdict, per-shot table, what to fix, next-session plan.",
    mock: <ReportMock />,
    route: "/analyze",
    bullets: [
      "Verdict + per-shot table",
      "What to fix, in plain language",
      "A plan for your next session",
    ],
  },
  {
    key: "plans",
    short: "Training",
    icon: ListChecks,
    tone: "sky",
    title: "Training plans",
    desc: "Drills and a weekly plan built around the weaknesses your analysis actually found.",
    mock: <TrainingPlanMock />,
    route: "/training",
    bullets: [
      "Weekly drill plan",
      "Built from your own analysis, not generic",
      "Track what's done each week",
    ],
  },
  {
    key: "gear",
    short: "Gear",
    icon: ShoppingBag,
    tone: "purple",
    title: "Gear recommendations",
    desc: "Racket, shoe and string picks matched to your play style, level and budget.",
    mock: <GearMock />,
    route: "/marketplace",
    bullets: [
      "Racket, shoe & string picks",
      "Matched to play style and budget",
      "Match score per item",
    ],
  },
  {
    key: "games",
    short: "Games",
    icon: Users,
    tone: "emerald",
    title: "Host & join games",
    desc: "Post a session, find players near you, and share it out in a tap.",
    mock: <GameMock />,
    route: "/community?host=1",
    bullets: [
      "Post a session in a couple taps",
      "Find players near you",
      "Share the invite out instantly",
    ],
  },
];

const TONES = {
  lime: { text: "text-lime-400", chip: "bg-lime-400/10 border-lime-400/25", glow: "group-hover:border-lime-400/40" },
  sky: { text: "text-sky-400", chip: "bg-sky-400/10 border-sky-400/25", glow: "group-hover:border-sky-400/40" },
  purple: { text: "text-purple-400", chip: "bg-purple-400/10 border-purple-400/25", glow: "group-hover:border-purple-400/40" },
  emerald: { text: "text-emerald-400", chip: "bg-emerald-400/10 border-emerald-400/25", glow: "group-hover:border-emerald-400/40" },
  amber: { text: "text-amber-400", chip: "bg-amber-400/10 border-amber-400/25", glow: "group-hover:border-amber-400/40" },
};

// One card: whole thing is a single focusable Link (keyboard + screen-reader
// friendly), with a "what you get" detail panel that:
//   - desktop: stays collapsed, opens on hover/focus with a smooth
//     ease-out-expo height/opacity reveal (no hover on touch, so this never
//     gates content mobile users can't reach).
//   - mobile: rendered open by default, condensed — most traffic is Android
//     phones, so the detail can't hide behind an interaction that doesn't
//     exist there.
function FeatureCard({ card, index, reduce, rise }) {
  const [active, setActive] = useState(false);
  const tone = TONES[card.tone];

  const detailVariants = {
    closed: { height: 0, opacity: 0, marginTop: 0 },
    open: { height: "auto", opacity: 1, marginTop: 16 },
  };

  return (
    <MotionLink
      to={card.route}
      custom={index}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={rise}
      whileHover={reduce ? undefined : { y: -6 }}
      whileTap={reduce ? undefined : { scale: 0.98 }}
      whileFocus={reduce ? undefined : { y: -6 }}
      transition={{ type: "tween", duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      onHoverStart={() => setActive(true)}
      onHoverEnd={() => setActive(false)}
      onFocus={() => setActive(true)}
      onBlur={() => setActive(false)}
      className={`group relative block rounded-3xl border border-zinc-800/80 bg-gradient-to-b from-zinc-900 to-zinc-900/40 p-5 md:p-6 transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950 ${tone.glow} ${card.span || ""}`}
    >
      {/* top hairline highlight */}
      <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

      <div className={`w-10 h-10 rounded-xl border flex items-center justify-center mb-4 ${tone.chip}`}>
        <card.icon className={`w-5 h-5 ${tone.text}`} strokeWidth={1.6} />
      </div>
      <h3 className="font-heading font-bold text-lg md:text-xl text-white tracking-tight mb-2">{card.title}</h3>
      <p className="text-zinc-400 text-sm leading-relaxed">{card.desc}</p>
      {card.note && (
        <p className="text-[11px] text-zinc-500 mt-2 border-l-2 border-zinc-700 pl-2">{card.note}</p>
      )}
      {card.mock && <div className="mt-5">{card.mock}</div>}

      {/* Desktop: collapsed detail, opens on hover/keyboard focus */}
      {card.bullets && (
        <motion.div
          className="hidden md:block overflow-hidden"
          initial={false}
          animate={active && !reduce ? "open" : "closed"}
          variants={detailVariants}
          transition={{ duration: reduce ? 0 : 0.45, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="pt-4 border-t border-zinc-800/80">
            <ul className="space-y-1.5 mb-3">
              {card.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-xs text-zinc-300">
                  <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${tone.text}`} strokeWidth={2.5} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <span className={`inline-flex items-center gap-1 text-xs font-semibold ${tone.text}`}>
              See it <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </motion.div>
      )}

      {/* Mobile: always-visible condensed detail — no hover on touch */}
      {card.bullets && (
        <div className="md:hidden mt-4 pt-4 border-t border-zinc-800/60">
          <ul className="space-y-1 mb-2.5">
            {card.bullets.slice(0, 2).map((b) => (
              <li key={b} className="flex items-start gap-1.5 text-[11px] text-zinc-400">
                <Check className={`w-3 h-3 mt-0.5 shrink-0 ${tone.text}`} strokeWidth={2.5} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${tone.text}`}>
            See it <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      )}
    </MotionLink>
  );
}

/**
 * Phones: one feature at a time behind a swipeable tab row.
 *
 * WHY: on desktop the nine cards sit in a 3-column grid and read as one
 * picture. On a phone the same grid became a single column nine cards tall —
 * every card with its own mockup — which is what made the page feel cluttered.
 * Tabs keep every feature one tap (or swipe) away without the scroll.
 */
function MobileFeatureTabs({ reduce }) {
  const [idx, setIdx] = useState(0);
  const railRef = useRef(null);
  const touchX = useRef(null);
  const card = CARDS[idx];
  const tone = TONES[card.tone];

  // Keep the active chip visible. Scroll the rail itself — scrollIntoView
  // would also yank the whole page vertically.
  useEffect(() => {
    const rail = railRef.current;
    const chip = rail?.children?.[idx];
    if (!rail || !chip) return;
    const target = chip.offsetLeft - (rail.clientWidth - chip.offsetWidth) / 2;
    rail.scrollTo({ left: Math.max(0, target), behavior: reduce ? "auto" : "smooth" });
  }, [idx, reduce]);

  const go = (d) => setIdx((i) => (i + d + CARDS.length) % CARDS.length);

  return (
    <div className="lg:hidden">
      <div
        ref={railRef}
        role="tablist"
        aria-label="Features"
        className="-mx-4 px-4 flex gap-2 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: "none" }}
      >
        {CARDS.map((c, i) => {
          const on = i === idx;
          const t = TONES[c.tone];
          return (
            <button
              key={c.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setIdx(i)}
              className={`shrink-0 inline-flex items-center gap-1.5 min-h-11 rounded-full border px-4 py-2 text-[13px] font-semibold transition-colors ${
                on ? `${t.chip} text-white` : "border-zinc-800 bg-zinc-900/60 text-zinc-300"
              }`}
            >
              <c.icon className={`w-3.5 h-3.5 ${on ? t.text : "text-zinc-400"}`} strokeWidth={1.8} />
              {c.short}
            </button>
          );
        })}
      </div>

      <div
        className="mt-3"
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchX.current == null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          touchX.current = null;
          if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
        }}
      >
        {/* Swap instantly and fade the new one in. An exit-then-enter
            sequence made every tap wait on the outgoing card first. */}
          <motion.div
            key={card.key}
            role="tabpanel"
            initial={reduce ? false : { opacity: 0.4, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-3xl border border-zinc-800/80 bg-gradient-to-b from-zinc-900 to-zinc-900/40 p-5"
          >
            <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 ${tone.chip}`}>
                <card.icon className={`w-5 h-5 ${tone.text}`} strokeWidth={1.6} />
              </div>
              <h3 className="font-heading font-bold text-lg text-white tracking-tight leading-tight">{card.title}</h3>
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed">{card.desc}</p>
            {card.mock && <div className="mt-4">{card.mock}</div>}
            <ul className="mt-4 space-y-1.5">
              {card.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-xs text-zinc-300">
                  <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${tone.text}`} strokeWidth={2.5} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <Link to={card.route} className={`mt-3 inline-flex items-center gap-1 min-h-11 text-sm font-semibold ${tone.text}`}>
              See it <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <button type="button" onClick={() => go(-1)} aria-label="Previous feature"
          className="w-11 h-11 rounded-full border border-zinc-800 bg-zinc-900/60 flex items-center justify-center text-zinc-300">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-1.5">
          {CARDS.map((c, i) => (
            <span key={c.key} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-lime-400" : "w-1.5 bg-zinc-700"}`} />
          ))}
        </div>
        <button type="button" onClick={() => go(1)} aria-label="Next feature"
          className="w-11 h-11 rounded-full border border-zinc-800 bg-zinc-900/60 flex items-center justify-center text-zinc-300">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function FeatureShowcase() {
  const reduce = useReducedMotion();
  // Match the landing page's premium scroll-in: ease-out-expo curve + a subtle
  // scale-settle, so the feature cards rise the same refined way the rest of
  // the page does rather than a plainer linear fade.
  const rise = reduce
    ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
    : {
        hidden: { opacity: 0, y: 36, scale: 0.96 },
        visible: (i = 0) => ({
          opacity: 1, y: 0, scale: 1,
          transition: { duration: 0.7, delay: Math.min(i, 5) * 0.07, ease: [0.16, 1, 0.3, 1] },
        }),
      };

  return (
    <section id="what-you-get" className="relative py-10 md:py-16 overflow-hidden bg-zinc-950">
      {/* layered depth */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-zinc-950 via-zinc-900/30 to-zinc-950" />
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[46rem] h-[46rem] max-w-full bg-lime-400/5 rounded-full blur-3xl" />

      <div className="relative container mx-auto px-4 max-w-6xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={rise}
          className="max-w-2xl mb-6 md:mb-16">
          <span className="inline-flex items-center gap-2 text-eyebrow uppercase text-lime-300 mb-4">
            <span className="w-8 h-px bg-lime-400/60" /> What you get
          </span>
          <h2 className="font-heading font-extrabold text-h2 text-white mb-5">
            One clip in.<br />
            <span className="text-lime-400">A whole coaching session out.</span>
          </h2>
          <p className="text-zinc-400 text-base md:text-lg leading-relaxed">
            Most people only ever try the upload. Here is everything that comes with it —
            all of it built, all of it in the app today.
          </p>
        </motion.div>

        <MobileFeatureTabs reduce={reduce} />

        {/* Tablet and desktop: the full grid, unchanged. */}
        <div className="hidden lg:grid lg:grid-cols-3 gap-4 md:gap-5">
          {CARDS.map((c, i) => (
            <FeatureCard key={c.key} card={c} index={i} reduce={reduce} rise={rise} />
          ))}
        </div>
      </div>
    </section>
  );
}
